import { useEffect, useRef, useState } from 'react'

/** `manual` means the browser has no Clipboard API or refused the write, and
 *  the caller shows the text in a pre-selected box to copy by hand. */
export type CopyState = 'idle' | 'copied' | 'manual'

/** How long "Copied" stays up. */
export const COPIED_MS = 2000

/** Copy-to-clipboard with the fallback every copy control here uses: on
 *  failure, a read-only box (`boxRef`) that is selected as soon as it shows,
 *  never an error. Keep the copy button mounted while the box is up, so
 *  `dismiss` can return focus to it (`buttonRef`). */
export function useCopyToClipboard() {
  const [state, setState] = useState<CopyState>('idle')
  const boxRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (state !== 'copied') return
    const t = setTimeout(() => setState('idle'), COPIED_MS)
    return () => clearTimeout(t)
  }, [state])

  useEffect(() => {
    if (state === 'manual') boxRef.current?.select()
  }, [state])

  async function copy(text: string) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard')
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch {
      setState('manual')
    }
  }

  return {
    state,
    copy,
    boxRef,
    buttonRef,
    /** Close the box when focus has already moved elsewhere (blur). */
    close: () => setState('idle'),
    /** Close the box and put focus back on the button (Escape). Without the
     *  focus call, focus falls to <body> as the box unmounts. */
    dismiss: () => {
      setState('idle')
      buttonRef.current?.focus()
    },
  }
}
